"use client";

import { createContext, useContext, useEffect, useState } from "react";
import type { Theme } from "./types";
import { defaultTheme } from "./default-theme";

const ThemeContext = createContext<{
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}>({
  theme: defaultTheme,
  isDark: false,
  toggleTheme: () => {},
});

export function ThemeProvider({
  theme,
  children,
}: {
  theme?: Theme;
  children: React.ReactNode;
}) {
  const [isDark, setIsDark] = useState(false);
  const activeTheme = theme ?? defaultTheme;

  useEffect(() => {
    // Check for system preference and saved preference
    const savedTheme = localStorage.getItem("theme");
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    
    const shouldBeDark = savedTheme === "dark" || (!savedTheme && systemPrefersDark);
    setIsDark(shouldBeDark);
    
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  return (
    <ThemeContext.Provider value={{ theme: activeTheme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
